/**
 * WebhooksCard — Outbound webhook subscription management.
 *
 * Mounted on /profile. Lets paid-tier users register HTTPS endpoints that
 * TurfPulse POSTs to when irrigation/agronomy events fire.
 *
 * UX contract:
 *   - Free users see the card but with a paid-tier upsell instead of the
 *     create form.
 *   - Raw HMAC secret (`whsec_…`) is shown ONCE in a persistent banner at
 *     creation time. After that only the metadata is reachable.
 *   - List shows label + URL + event chips + last delivery + status pill
 *     (active / disabled / auto-disabled).
 *   - Test ping button fires a synthetic event and shows the response code.
 *   - Toggle re-enables auto-disabled subscriptions.
 *   - Delete is hard-delete (with confirm).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Plug as Webhook, Copy, Check, Trash, Plus, Lightning, Crown, Warning, ToggleLeft, ToggleRight } from '@phosphor-icons/react';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL } from '../config/api';

const fmt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
};

export const WebhooksCard = () => {
  const [subs, setSubs] = useState([]);
  const [events, setEvents] = useState([]);
  const [isPaidTier, setIsPaidTier] = useState(false);
  const [maxSubs, setMaxSubs] = useState(5);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ label: 'Home Assistant Box', target_url: '', events: [] });
  const [justCreated, setJustCreated] = useState(null);
  const [copied, setCopied] = useState(false);
  const [testing, setTesting] = useState(null);  // id of subscription currently being tested

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, evRes] = await Promise.all([
        axios.get(`${API_URL}/api/profile/webhooks`, { withCredentials: true }),
        axios.get(`${API_URL}/api/profile/webhooks/events`),
      ]);
      setSubs(listRes.data?.subscriptions || []);
      setIsPaidTier(!!listRes.data?.is_paid_tier);
      setMaxSubs(listRes.data?.max_subscriptions || 5);
      setEvents(evRes.data?.events || []);
    } catch {
      // Silent — parent profile page handles auth redirect
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleToggleEvent = (evId) => {
    setForm((f) => ({
      ...f,
      events: f.events.includes(evId)
        ? f.events.filter((e) => e !== evId)
        : [...f.events, evId],
    }));
  };

  const handleCreate = async () => {
    if (!form.label.trim() || !form.target_url.trim()) {
      toast.error('Label and target URL are required.');
      return;
    }
    if (form.events.length === 0) {
      toast.error('Pick at least one event.');
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/profile/webhooks`,
        form,
        { withCredentials: true },
      );
      setJustCreated({ raw_secret: res.data.raw_secret, label: res.data.subscription.label });
      setShowCreate(false);
      setForm({ label: 'Home Assistant Box', target_url: '', events: [] });
      await load();
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.detail || 'Could not create subscription.';
      if (status === 402) {
        toast.error('Webhooks require a paid tier. Become a Founding Member to enable.');
      } else {
        toast.error(msg);
      }
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(justCreated.raw_secret);
      setCopied(true);
      toast.success('Secret copied. Save it in HA secrets.yaml — you won\'t see it again.');
      setTimeout(() => setCopied(false), 2500);
    } catch { toast.error('Copy failed — select the text manually.'); }
  };

  const handleTest = async (id) => {
    setTesting(id);
    try {
      const res = await axios.post(
        `${API_URL}/api/profile/webhooks/${id}/test`,
        {},
        { withCredentials: true },
      );
      if (res.data?.delivered) {
        toast.success(`Test delivered · HTTP ${res.data.status_code}`);
      } else {
        toast.error(`Test failed · ${res.data?.error || 'unknown error'}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test failed.');
    } finally {
      setTesting(null);
    }
  };

  const handleToggle = async (id) => {
    try {
      await axios.put(`${API_URL}/api/profile/webhooks/${id}/toggle`, {}, { withCredentials: true });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Toggle failed.');
    }
  };

  const handleDelete = async (id, label) => {
    if (!window.confirm(`Delete "${label}"? Anything depending on this webhook will stop receiving events.`)) return;
    try {
      await axios.delete(`${API_URL}/api/profile/webhooks/${id}`, { withCredentials: true });
      toast.success('Webhook deleted.');
      await load();
    } catch { toast.error('Delete failed.'); }
  };

  const activeCount = subs.filter((s) => s.enabled).length;

  return (
    <Card data-testid="webhooks-card" className="border-emerald-500/30">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Webhook className="w-5 h-5 text-emerald-600" weight="duotone" />
          Outbound Webhooks
          {!isPaidTier && (
            <Badge className="text-[9px] uppercase bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30 ml-auto">
              <Crown size={10} className="mr-1" weight="fill" /> Inner Circle
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          Real-time push events to your Home Assistant box, n8n flow, or any HTTPS endpoint. TurfPulse POSTs HMAC-signed
          payloads when schedules fire, MAD breaches, dewpoint locks, or spray windows open.{' '}
          <a href="/integrations/home-assistant#webhooks" className="text-emerald-600 underline">HA setup guide →</a>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Just-created secret banner */}
        {justCreated && (
          <div className="rounded-lg border-2 border-emerald-500/60 bg-emerald-500/5 p-3 space-y-2" data-testid="webhooks-just-created">
            <div className="flex items-center gap-2 text-sm font-semibold text-emerald-700 dark:text-emerald-300">
              <Warning className="w-4 h-4" weight="duotone" />
              Save this signing secret NOW — you won't see it again
            </div>
            <div className="text-xs text-muted-foreground">
              Webhook: <span className="font-mono">{justCreated.label}</span>
            </div>
            <div className="flex items-stretch gap-2">
              <code className="flex-1 text-xs font-mono p-2 rounded bg-zinc-900 text-emerald-300 break-all select-all" data-testid="webhooks-raw-secret">
                {justCreated.raw_secret}
              </code>
              <Button variant="outline" size="sm" onClick={handleCopy} className="shrink-0" data-testid="webhooks-copy-btn">
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            <button type="button" className="text-xs text-muted-foreground underline hover:text-foreground"
              onClick={() => { setJustCreated(null); setCopied(false); }}
              data-testid="webhooks-dismiss-banner">
              I've saved it — dismiss
            </button>
          </div>
        )}

        {/* Subscription list */}
        {loading ? (
          <div className="text-xs text-muted-foreground">Loading…</div>
        ) : subs.length === 0 ? (
          <div className="text-xs text-muted-foreground italic" data-testid="webhooks-empty">
            No webhook subscriptions yet.{' '}
            {isPaidTier
              ? 'Add one to start pushing events to HA / n8n / your dashboard.'
              : 'Become a Founding Member to enable real-time event delivery.'}
          </div>
        ) : (
          <div className="space-y-2">
            {subs.map((s) => {
              const isAutoDisabled = !s.enabled && (s.consecutive_failures || 0) >= 10;
              return (
                <div key={s.id} className="p-3 rounded border border-border bg-muted/20 space-y-2" data-testid={`webhook-row-${s.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm truncate">{s.label}</span>
                        {isAutoDisabled ? (
                          <Badge variant="outline" className="text-[9px] uppercase border-rose-500/40 text-rose-700 dark:text-rose-300">
                            Auto-disabled · {s.consecutive_failures} fails
                          </Badge>
                        ) : s.enabled ? (
                          <Badge className="text-[9px] uppercase bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30">
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-[9px] uppercase">Disabled</Badge>
                        )}
                      </div>
                      <code className="text-[11px] font-mono text-muted-foreground break-all">{s.target_url}</code>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(s.events || []).map((e) => (
                          <span key={e} className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 font-mono">
                            {e}
                          </span>
                        ))}
                      </div>
                      <div className="text-[10px] text-muted-foreground mt-1">
                        Last delivery: {fmt(s.last_delivery_at)}
                        {s.last_status_code && ` · HTTP ${s.last_status_code}`}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      <Button size="sm" variant="ghost" onClick={() => handleTest(s.id)} disabled={testing === s.id}
                        data-testid={`webhook-test-${s.id}`} title="Send test event">
                        <Lightning className="w-4 h-4 text-amber-600" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleToggle(s.id)}
                        data-testid={`webhook-toggle-${s.id}`} title={s.enabled ? 'Disable' : 'Enable'}>
                        {s.enabled
                          ? <ToggleRight className="w-5 h-5 text-emerald-600" weight="fill" />
                          : <ToggleLeft className="w-5 h-5 text-muted-foreground" />}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(s.id, s.label)}
                        data-testid={`webhook-delete-${s.id}`} title="Delete">
                        <Trash className="w-4 h-4 text-rose-600" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Create form / paid-tier gate */}
        {!isPaidTier ? (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-foreground space-y-2"
            data-testid="webhooks-paid-gate">
            <div className="flex items-center gap-2 font-semibold">
              <Crown className="w-4 h-4 text-emerald-600" weight="fill" />
              Webhooks are an Inner Circle / paid-tier feature
            </div>
            <p className="text-muted-foreground">
              Founding members get up to {maxSubs} active webhook subscriptions, HMAC-signed delivery, and the closed-loop HA experience. Free through Oct 31, 2026 · then $4.99/mo locked for life.
            </p>
            <Button asChild variant="outline" size="sm" data-testid="webhooks-upsell-cta">
              <a href="/integrations/home-assistant#inner-circle">See Inner Circle perks →</a>
            </Button>
          </div>
        ) : !showCreate ? (
          <Button variant="outline" onClick={() => setShowCreate(true)} disabled={activeCount >= maxSubs}
            data-testid="webhooks-new-btn">
            <Plus className="w-4 h-4 mr-1.5" />
            {activeCount >= maxSubs ? `Cap reached (${maxSubs} active)` : 'Add webhook subscription'}
          </Button>
        ) : (
          <div className="space-y-3 p-3 rounded border border-border bg-muted/20">
            <div className="space-y-1">
              <Label htmlFor="wh-label" className="text-xs">Label</Label>
              <Input id="wh-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="Home Assistant Box" data-testid="webhook-label-input" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="wh-url" className="text-xs">Target URL (https://)</Label>
              <Input id="wh-url" value={form.target_url} onChange={(e) => setForm({ ...form, target_url: e.target.value })}
                placeholder="https://my-ha.example.com/api/webhook/turfpulse" data-testid="webhook-url-input" />
              <p className="text-[10px] text-muted-foreground">http:// allowed for localhost / *.local / *.lan / 192.168.* / 10.* / 172.*</p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Events</Label>
              <div className="grid sm:grid-cols-2 gap-1.5">
                {events.map((ev) => (
                  <label key={ev.id} className="flex items-start gap-2 p-2 rounded border border-border hover:border-emerald-500/40 cursor-pointer text-xs"
                    data-testid={`webhook-event-${ev.id}`}>
                    <input type="checkbox" checked={form.events.includes(ev.id)} onChange={() => handleToggleEvent(ev.id)}
                      className="mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="font-mono font-semibold">{ev.id}</div>
                      <div className="text-[10px] text-muted-foreground leading-snug">{ev.description}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleCreate} disabled={creating} size="sm" data-testid="webhook-create-btn">
                {creating ? 'Creating…' : 'Create webhook'}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setShowCreate(false)} disabled={creating}>Cancel</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default WebhooksCard;
